'use client'

import { useState, useEffect, useRef } from 'react'
import { Clock } from 'lucide-react'

interface HoldTimerProps {
  expiresAt: Date | null
  onExpired: () => void
  onExtendRequest?: () => void
  onManualExtend?: () => Promise<void>
}

export default function HoldTimer({ expiresAt, onExpired, onExtendRequest, onManualExtend }: HoldTimerProps) {
  const [remaining, setRemaining] = useState<number | null>(null)
  const [extending, setExtending] = useState(false)
  const [lastExtendTime, setLastExtendTime] = useState<number | null>(null)
  const expiredCalledRef = useRef(false)
  const extendRequestedRef = useRef(false)
  // Store expiresAt in a ref so the interval tick always reads the latest
  // value without restarting the interval
  const expiresAtRef = useRef<Date | null>(expiresAt)

  // Update the ref when prop changes, but only reset "expired" flags
  // if the new expiry is actually later (a real extension)
  useEffect(() => {
    const prev = expiresAtRef.current
    expiresAtRef.current = expiresAt

    if (expiresAt && prev && expiresAt.getTime() > prev.getTime()) {
      // Got a genuine extension — allow auto-extend to fire again later
      extendRequestedRef.current = false
    }
    if (!prev && expiresAt) {
      // First time getting an expiry
      expiredCalledRef.current = false
      extendRequestedRef.current = false
    }
  }, [expiresAt])

  const onExpiredRef = useRef(onExpired)
  onExpiredRef.current = onExpired
  const onExtendRequestRef = useRef(onExtendRequest)
  onExtendRequestRef.current = onExtendRequest

  const handleManualExtend = async () => {
    if (!onManualExtend || extending) return

    // Check cooldown (1 minute = 60000ms)
    const now = Date.now()
    if (lastExtendTime && (now - lastExtendTime) < 60000) {
      return
    }

    setExtending(true)
    try {
      await onManualExtend()
      setLastExtendTime(now)
    } catch (error) {
      console.error('Failed to extend hold:', error)
    } finally {
      setExtending(false)
    }
  }

  // Single stable interval that reads expiresAt from the ref
  useEffect(() => {
    if (!expiresAt) {
      setRemaining(null)
      return
    }

    function tick() {
      const exp = expiresAtRef.current
      if (!exp) return

      const ms = exp.getTime() - Date.now()
      if (ms <= 0) {
        setRemaining(0)
        if (!expiredCalledRef.current) {
          expiredCalledRef.current = true
          onExpiredRef.current()
        }
        return
      }
      const secs = Math.ceil(ms / 1000)
      setRemaining(secs)

      // Auto-request extension at 2 minutes remaining (once per extension cycle)
      if (secs <= 120 && secs > 115 && !extendRequestedRef.current && onExtendRequestRef.current) {
        extendRequestedRef.current = true
        onExtendRequestRef.current()
      }
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
    // Only start/stop the interval when expiresAt goes from null↔non-null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!expiresAt])

  if (remaining === null || remaining < 0) return null

  const minutes = Math.floor(remaining / 60)
  const seconds = remaining % 60
  const display = `${minutes}:${seconds.toString().padStart(2, '0')}`

  const isWarning = remaining <= 120 && remaining > 30
  const isCritical = remaining <= 30
  const showExtendButton = remaining <= 180 && onManualExtend

  // Calculate cooldown remaining
  const now = Date.now()
  const cooldownRemaining = lastExtendTime ? Math.max(0, 60000 - (now - lastExtendTime)) : 0
  const isOnCooldown = cooldownRemaining > 0

  let colorClass = 'text-text-secondary'
  let bgClass = 'bg-surface-secondary'
  if (isCritical) {
    colorClass = 'text-error'
    bgClass = 'bg-error/10'
  } else if (isWarning) {
    colorClass = 'text-warning'
    bgClass = 'bg-warning/10'
  }

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${bgClass}`}>
      <Clock className={`w-4 h-4 flex-shrink-0 ${colorClass}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium ${colorClass}`}>
          Seats held for <span className="tabular-nums font-mono">{display}</span>
        </p>
        {isCritical && (
          <p className="text-xs text-error/70 mt-0.5">
            Select quickly or your holds will expire
          </p>
        )}
      </div>
      {showExtendButton && (
        <button
          onClick={handleManualExtend}
          disabled={extending || isOnCooldown}
          className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-text-inverse hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          title={isOnCooldown ? `Wait ${Math.ceil(cooldownRemaining / 1000)}s to extend again` : 'Extend your hold time'}
        >
          {extending ? 'Extending...' : isOnCooldown ? `Wait ${Math.ceil(cooldownRemaining / 1000)}s` : 'Extend Hold'}
        </button>
      )}
    </div>
  )
}
